import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { couponsApi, type Coupon, type CouponInput, type CouponRedemption, type CouponType } from '@/services/api';

const errMsg = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error || fallback;

const money = (n: number) => `${n.toFixed(2)} €`;
const day = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-ES') : '');
const toDateInput = (iso?: string | null) => (iso ? iso.slice(0, 10) : '');
const describe = (c: Pick<Coupon, 'type' | 'value'>) => (c.type === 'percent' ? `${c.value} %` : money(c.value));

const REDEMPTION_LABEL: Record<CouponRedemption['status'], string> = {
  reserved: 'Reservado',
  redeemed: 'Canjeado',
  released: 'Liberado',
};

/** Pestaña Cupones de Ecommerce (VIDA-350). */
export default function CouponsEditor({ canUpdate }: { canUpdate: boolean }) {
  const [coupons, setCoupons] = useState<Coupon[] | null>(null);
  const [editing, setEditing] = useState<Coupon | 'new' | null>(null);
  const [usesOf, setUsesOf] = useState<Coupon | null>(null);

  const load = async () => {
    try { setCoupons((await couponsApi.list()).data.coupons || []); }
    catch (e) { toast.error(errMsg(e, 'No se pudieron cargar los cupones')); setCoupons([]); }
  };
  useEffect(() => { void load(); }, []);

  const toggleActive = async (c: Coupon) => {
    try { await couponsApi.update(c._id, { active: !c.active }); void load(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo cambiar')); }
  };
  const remove = async (c: Coupon) => {
    if (!confirm(`¿Borrar el cupón ${c.code}?`)) return;
    try { await couponsApi.remove(c._id); toast.success('Cupón borrado'); void load(); }
    catch (e) { toast.error(errMsg(e, 'No se pudo borrar')); }
  };

  return (
    <div className="card">
      <div className="p-6 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cupones</h2>
            <p className="text-sm text-gray-500 mt-1">
              Un cupón por pedido. El descuento no se aplica al envío, y el envío gratis se calcula sobre el total ya descontado.
            </p>
          </div>
          {canUpdate && (
            <button onClick={() => setEditing('new')} className="btn-primary inline-flex items-center gap-2">
              <PlusIcon className="w-5 h-5" /> Nuevo cupón
            </button>
          )}
        </div>

        {coupons === null ? (
          <p className="text-sm text-gray-400">Cargando…</p>
        ) : coupons.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no hay cupones.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Código</th>
                  <th className="py-2 pr-4">Descuento</th>
                  <th className="py-2 pr-4">Condiciones</th>
                  <th className="py-2 pr-4">Usos</th>
                  <th className="py-2 pr-4">Estado</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {coupons.map((c) => {
                  const u = c.usage || { redeemed: 0, reserved: 0, discount: 0 };
                  const conditions = [
                    c.min_subtotal > 0 ? `desde ${money(c.min_subtotal)}` : '',
                    c.starts_at || c.ends_at ? `${day(c.starts_at) || '…'} → ${day(c.ends_at) || '…'}` : '',
                    c.max_uses_per_customer ? `${c.max_uses_per_customer} por cliente` : '',
                    c.stackable ? 'acumulable' : 'no en rebajados',
                  ].filter(Boolean).join(' · ');
                  return (
                    <tr key={c._id} className="border-b border-gray-100 align-top">
                      <td className="py-2 pr-4">
                        <button onClick={() => setEditing(c)} className="font-mono font-semibold text-gray-900 hover:text-vida-700">{c.code}</button>
                        {c.description && <p className="text-xs text-gray-500">{c.description}</p>}
                      </td>
                      <td className="py-2 pr-4">{describe(c)}</td>
                      <td className="py-2 pr-4 text-gray-600">{conditions}</td>
                      <td className="py-2 pr-4">
                        <button onClick={() => setUsesOf(c)} className="text-left hover:text-vida-700">
                          {u.redeemed}{c.max_uses ? ` / ${c.max_uses}` : ''} canjeados
                          {u.reserved > 0 && <span className="block text-xs text-gray-500">{u.reserved} en curso</span>}
                          {u.discount > 0 && <span className="block text-xs text-gray-500">{money(u.discount)} descontados</span>}
                        </button>
                      </td>
                      <td className="py-2 pr-4">
                        <button onClick={() => canUpdate && void toggleActive(c)} disabled={!canUpdate}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {c.active ? 'Activo' : 'Inactivo'}
                        </button>
                      </td>
                      <td className="py-2 text-right">
                        {canUpdate && (
                          <button onClick={() => void remove(c)} aria-label={`Borrar ${c.code}`} className="p-1 text-gray-300 hover:text-red-600">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <CouponForm coupon={editing === 'new' ? null : editing} readOnly={!canUpdate}
          onClose={() => setEditing(null)} onSaved={() => { setEditing(null); void load(); }} />
      )}
      {usesOf && <RedemptionsModal coupon={usesOf} onClose={() => setUsesOf(null)} />}
    </div>
  );
}

function CouponForm({ coupon, readOnly, onClose, onSaved }: {
  coupon: Coupon | null; readOnly: boolean; onClose: () => void; onSaved: () => void;
}) {
  const [form, setForm] = useState({
    code: coupon?.code || '',
    description: coupon?.description || '',
    type: (coupon?.type || 'percent') as CouponType,
    value: coupon ? String(coupon.value) : '',
    min_subtotal: coupon?.min_subtotal ? String(coupon.min_subtotal) : '',
    starts_at: toDateInput(coupon?.starts_at),
    ends_at: toDateInput(coupon?.ends_at),
    max_uses: coupon?.max_uses ? String(coupon.max_uses) : '',
    max_uses_per_customer: coupon?.max_uses_per_customer ? String(coupon.max_uses_per_customer) : '',
    stackable: coupon?.stackable ?? false,
    active: coupon?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<typeof form>) => setForm({ ...form, ...patch });
  const num = (v: string) => (v.trim() === '' ? null : Number(v));

  const submit = async () => {
    const value = Number(form.value);
    if (!form.code.trim()) { toast.error('Pon un código'); return; }
    if (!(value > 0)) { toast.error('El descuento tiene que ser mayor que 0'); return; }
    const data: CouponInput = {
      code: form.code.trim(),
      description: form.description.trim(),
      type: form.type,
      value,
      min_subtotal: num(form.min_subtotal) ?? 0,
      // Fin de día incluido: un cupón «hasta el 31» vale todo el día 31.
      starts_at: form.starts_at ? new Date(`${form.starts_at}T00:00:00`).toISOString() : null,
      ends_at: form.ends_at ? new Date(`${form.ends_at}T23:59:59`).toISOString() : null,
      max_uses: num(form.max_uses),
      max_uses_per_customer: num(form.max_uses_per_customer),
      stackable: form.stackable,
      active: form.active,
    };
    setBusy(true);
    try {
      if (coupon) await couponsApi.update(coupon._id, data);
      else await couponsApi.create(data);
      toast.success(coupon ? 'Cupón guardado' : 'Cupón creado');
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'No se pudo guardar')); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label="Cupón">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{coupon ? `Cupón ${coupon.code}` : 'Nuevo cupón'}</h3>
        <fieldset disabled={readOnly} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="cp-code">Código</label>
            <input id="cp-code" className="input font-mono uppercase" maxLength={40} value={form.code} onChange={(e) => set({ code: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-type">Tipo</label>
            <select id="cp-type" className="input" value={form.type} onChange={(e) => set({ type: e.target.value as CouponType })}>
              <option value="percent">Porcentaje</option>
              <option value="fixed">Importe fijo (€)</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="cp-value">{form.type === 'percent' ? 'Porcentaje' : 'Importe (€)'}</label>
            <input id="cp-value" type="number" min="0" step="0.01" max={form.type === 'percent' ? 100 : undefined} className="input"
              value={form.value} onChange={(e) => set({ value: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-min">Pedido mínimo (€)</label>
            <input id="cp-min" type="number" min="0" step="0.01" className="input" placeholder="Sin mínimo"
              value={form.min_subtotal} onChange={(e) => set({ min_subtotal: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <label className="label" htmlFor="cp-desc">Descripción (la ve el cliente)</label>
            <input id="cp-desc" className="input" maxLength={300} value={form.description} onChange={(e) => set({ description: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-start">Desde</label>
            <input id="cp-start" type="date" className="input" value={form.starts_at} onChange={(e) => set({ starts_at: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-end">Hasta (incluido)</label>
            <input id="cp-end" type="date" className="input" value={form.ends_at} onChange={(e) => set({ ends_at: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-max">Usos en total</label>
            <input id="cp-max" type="number" min="1" step="1" className="input" placeholder="Sin límite"
              value={form.max_uses} onChange={(e) => set({ max_uses: e.target.value })} />
          </div>
          <div>
            <label className="label" htmlFor="cp-max-c">Usos por cliente</label>
            <input id="cp-max-c" type="number" min="1" step="1" className="input" placeholder="Sin límite"
              value={form.max_uses_per_customer} onChange={(e) => set({ max_uses_per_customer: e.target.value })} />
          </div>
          <label className="sm:col-span-2 flex items-start gap-2 text-sm text-gray-700">
            <input type="checkbox" className="mt-1" checked={form.stackable} onChange={(e) => set({ stackable: e.target.checked })} />
            <span>Acumulable: también descuenta productos que ya están rebajados.</span>
          </label>
          <label className="sm:col-span-2 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} /> Activo
          </label>
        </fieldset>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">{readOnly ? 'Cerrar' : 'Cancelar'}</button>
          {!readOnly && <button onClick={submit} disabled={busy} className="btn-primary disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>}
        </div>
      </div>
    </div>
  );
}

function RedemptionsModal({ coupon, onClose }: { coupon: Coupon; onClose: () => void }) {
  const [rows, setRows] = useState<CouponRedemption[] | null>(null);
  useEffect(() => {
    couponsApi.redemptions(coupon._id).then((r) => setRows(r.data.redemptions || [])).catch(() => setRows([]));
  }, [coupon._id]);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="card w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto" role="dialog" aria-modal="true" aria-label={`Usos de ${coupon.code}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Usos de {coupon.code}</h3>
        <p className="text-xs text-gray-500 mb-4">«Reservado» es un pedido sin pagar todavía; si no se paga en 30 minutos deja de contar.</p>
        {rows === null ? <p className="text-sm text-gray-400">Cargando…</p> : rows.length === 0 ? (
          <p className="text-sm text-gray-500">Este cupón todavía no se ha usado.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4">Fecha</th><th className="py-2 pr-4">Pedido</th><th className="py-2 pr-4">Cliente</th>
                <th className="py-2 pr-4">Descuento</th><th className="py-2">Estado</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id} className="border-b border-gray-100">
                    <td className="py-2 pr-4">{new Date(r.created_at).toLocaleString('es-ES')}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{r.order_number}</td>
                    <td className="py-2 pr-4">{r.email}</td>
                    <td className="py-2 pr-4">{money(r.discount)}</td>
                    <td className="py-2">{REDEMPTION_LABEL[r.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="flex justify-end mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cerrar</button>
        </div>
      </div>
    </div>
  );
}
